import { useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, FileText, Plus, Trash2 } from 'lucide-react';
import { seedWorkspace, type PageNode } from '@nexus/store';
import { useStore } from '../db/context';
import { useUi } from '../state/ui';
import { cn } from '../lib/cn';
import { usePageActions } from '../lib/pageActions';
import { PageMenu } from './PageMenu';
import { PageIcon } from './PageIcon';

type DropPos = 'before' | 'inside' | 'after';

interface FlatRow {
  node: PageNode;
  depth: number;
  parentId: string | null;
}

/** Visible rows in display order, honouring collapsed state. */
function flatten(
  nodes: PageNode[],
  expanded: Record<string, boolean>,
  depth = 0,
  out: FlatRow[] = [],
) {
  for (const node of nodes) {
    out.push({ node, depth, parentId: node.parentId });
    if (expanded[node.id] && node.children.length) flatten(node.children, expanded, depth + 1, out);
  }
  return out;
}

export function PageTree({
  tree,
  activePageId,
  readOnly,
  emptyMessage,
  showSeedSample = true,
}: {
  tree: PageNode[];
  activePageId: string | null;
  /** A read-only workspace membership: no add/rename/move/trash/drag. */
  readOnly?: boolean;
  /** Shown instead of the default when there are no pages yet. */
  emptyMessage?: string;
  /** The demo content link: only for the personal tree, never a workspace's. */
  showSeedSample?: boolean;
}) {
  const { t } = useTranslation();
  const store = useStore();
  const { expanded, toggleExpanded, open } = useUi();
  const rows = flatten(tree, expanded);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const actions = usePageActions();
  const [drag, setDrag] = useState<{ id: string; over: string | null; pos: DropPos } | null>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());

  const focusRow = (id: string) => {
    setFocusId(id);
    rowRefs.current.get(id)?.focus();
  };

  const addChild = async (parentId: string) => {
    const page = await store.createPage({ title: '', parentId });
    toggleExpanded(parentId, true);
    open({ kind: 'page', pageId: page.id });
  };

  const onKeyDown = (e: KeyboardEvent, i: number) => {
    const row = rows[i]!;
    const { node } = row;
    const hasKids = node.children.length > 0;
    // The right-click menu's shortcuts (see PAGE_KEYS); none of them in read-only.
    const mod = (e.ctrlKey || e.metaKey) && !readOnly;
    const key = e.key.toLowerCase();
    const shortcut =
      mod && !e.shiftKey && key === 'd'
        ? () => void actions.duplicate(node.id)
        : mod && e.shiftKey && key === 'r'
          ? () => setRenaming(node.id)
          : mod && e.shiftKey && key === 'p'
            ? () => actions.move(node.id)
            : mod && e.shiftKey && key === 'enter'
              ? () => actions.newTab(node.id)
              : null;
    if (shortcut) {
      e.preventDefault();
      shortcut();
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        if (rows[i + 1]) focusRow(rows[i + 1]!.node.id);
        break;
      case 'ArrowUp':
        if (rows[i - 1]) focusRow(rows[i - 1]!.node.id);
        break;
      case 'ArrowRight':
        if (hasKids && !expanded[node.id]) toggleExpanded(node.id, true);
        else if (hasKids) focusRow(node.children[0]!.id);
        break;
      case 'ArrowLeft':
        if (hasKids && expanded[node.id]) toggleExpanded(node.id, false);
        else if (row.parentId) focusRow(row.parentId);
        break;
      case 'Enter':
        open({ kind: 'page', pageId: node.id }, e.ctrlKey || e.metaKey);
        break;
      case 'F2':
        if (!readOnly) setRenaming(node.id);
        break;
      case 'Delete':
        if (!readOnly) void store.trashPage(node.id);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  const dropPos = (e: DragEvent<HTMLElement>): DropPos => {
    const r = e.currentTarget.getBoundingClientRect();
    const y = (e.clientY - r.top) / r.height;
    return y < 0.25 ? 'before' : y > 0.75 ? 'after' : 'inside';
  };

  // The dragged page and the drop position come from refs and the drop event
  // itself, not from state: a drop right after the last dragover would
  // otherwise use the position of the render before (e.g. "after" when the
  // pointer had just entered from below).
  const dragged = useRef<string | null>(null);
  const onDrop = async (target: FlatRow, pos: DropPos) => {
    const id = dragged.current;
    dragged.current = null;
    setDrag(null);
    if (!id || id === target.node.id) return;
    const drag = { id };
    try {
      if (pos === 'inside') {
        const last = target.node.children[target.node.children.length - 1];
        await store.movePage(drag.id, target.node.id, last?.id ?? null);
        toggleExpanded(target.node.id, true);
      } else {
        const siblings = rows.filter(
          (r) => r.parentId === target.parentId && r.node.id !== drag.id,
        );
        const idx = siblings.findIndex((r) => r.node.id === target.node.id);
        const after = pos === 'after' ? target.node.id : (siblings[idx - 1]?.node.id ?? null);
        await store.movePage(drag.id, target.parentId, after);
      }
    } catch {
      // Dropping a page into its own subtree is refused by the store; nothing to do.
    }
  };

  if (rows.length === 0) {
    return (
      <div className="px-3 py-2 text-sm text-fg-muted">
        <p>{emptyMessage ?? t('sidebar.empty')}</p>
        {showSeedSample && (
          <button
            type="button"
            onClick={() => void seedWorkspace(store)}
            className="mt-2 text-accent-ink underline underline-offset-2"
          >
            {t('sidebar.loadSample')}
          </button>
        )}
      </div>
    );
  }

  const tabStop = focusId && rows.some((r) => r.node.id === focusId) ? focusId : rows[0]!.node.id;

  return (
    <div role="tree" aria-label={t('sidebar.pages')} className="flex flex-col py-1">
      {rows.map((row, i) => {
        const { node, depth } = row;
        const title = node.title || t('page.untitled');
        const hasKids = node.children.length > 0;
        const isOpen = !!expanded[node.id];
        const isDropTarget = drag?.over === node.id;
        return (
          <PageMenu
            key={node.id}
            pageId={node.id}
            title={title}
            workspaceId={node.workspaceId}
            readOnly={readOnly}
            onRename={readOnly ? undefined : () => setRenaming(node.id)}
            onAddChild={readOnly ? undefined : () => void addChild(node.id)}
          >
            <div
              ref={(el) => {
                if (el) rowRefs.current.set(node.id, el);
                else rowRefs.current.delete(node.id);
              }}
              role="treeitem"
              aria-label={title}
              aria-level={depth + 1}
              aria-expanded={hasKids ? isOpen : undefined}
              aria-selected={node.id === activePageId}
              tabIndex={node.id === tabStop ? 0 : -1}
              draggable={!readOnly && renaming !== node.id}
              onFocus={() => setFocusId(node.id)}
              onKeyDown={(e) => e.target === e.currentTarget && onKeyDown(e, i)}
              onClick={(e) => open({ kind: 'page', pageId: node.id }, e.ctrlKey || e.metaKey)}
              onDoubleClick={() => !readOnly && setRenaming(node.id)}
              onDragStart={(e) => {
                if (readOnly) return;
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', node.id);
                dragged.current = node.id;
                setDrag({ id: node.id, over: null, pos: 'inside' });
              }}
              onDragOver={(e) => {
                const id = dragged.current;
                if (!id) return;
                e.preventDefault();
                const pos = dropPos(e);
                if (drag?.over !== node.id || drag.pos !== pos) setDrag({ id, over: node.id, pos });
              }}
              onDragEnd={() => {
                dragged.current = null;
                setDrag(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                void onDrop(row, dropPos(e));
              }}
              data-testid="tree-row"
              className={cn(
                'group relative mx-2 flex h-8 cursor-pointer items-center gap-1 rounded-lg pr-1 text-sm select-none',
                node.id === activePageId ? 'bg-bg font-medium shadow-sheet' : 'hover:bg-bg-hover',
                isDropTarget && drag.pos === 'inside' && 'ring-2 ring-accent',
              )}
              style={{ paddingLeft: 4 + depth * 14 }}
            >
              {isDropTarget && drag.pos !== 'inside' && (
                <span
                  aria-hidden
                  className={cn(
                    'absolute right-1 left-1 h-0.5 bg-accent',
                    drag.pos === 'before' ? 'top-0' : 'bottom-0',
                  )}
                />
              )}
              <button
                type="button"
                tabIndex={-1}
                aria-label={isOpen ? t('sidebar.collapse') : t('sidebar.expand')}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(node.id);
                }}
                className={cn(
                  'grid size-5 place-items-center rounded hover:bg-bg-active',
                  !hasKids && 'invisible',
                )}
              >
                <ChevronRight
                  className={cn('size-3.5 transition-transform', isOpen && 'rotate-90')}
                />
              </button>
              <span
                aria-hidden
                className={cn(
                  'grid size-5 place-items-center',
                  hasKids && !node.icon ? 'mod-projects mod-ink' : 'text-fg-muted',
                )}
              >
                <PageIcon icon={node.icon} fallback={<FileText className="size-4" />} />
              </span>
              {!readOnly && renaming === node.id ? (
                <input
                  autoFocus
                  defaultValue={node.title}
                  aria-label={t('page.rename')}
                  className="min-w-0 flex-1 rounded-md border border-border bg-bg px-1 outline-none"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') {
                      e.currentTarget.value = node.title;
                      e.currentTarget.blur();
                    }
                  }}
                  onBlur={(e) => {
                    setRenaming(null);
                    const value = e.currentTarget.value.trim();
                    if (value !== node.title) void store.updatePage(node.id, { title: value });
                    focusRow(node.id);
                  }}
                />
              ) : (
                <span className={cn('min-w-0 flex-1 truncate', !node.title && 'text-fg-muted')}>
                  {title}
                </span>
              )}
              {!readOnly && (
                <span className="hidden items-center group-focus-within:flex group-hover:flex">
                  <button
                    type="button"
                    tabIndex={-1}
                    title={t('sidebar.newSubpage')}
                    aria-label={t('sidebar.newSubpage')}
                    onClick={(e) => {
                      e.stopPropagation();
                      void addChild(node.id);
                    }}
                    className="grid size-5 place-items-center rounded text-fg-muted hover:bg-bg-active hover:text-fg"
                  >
                    <Plus className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    tabIndex={-1}
                    title={t('page.moveToTrash')}
                    aria-label={t('page.moveToTrash')}
                    onClick={(e) => {
                      e.stopPropagation();
                      void store.trashPage(node.id);
                    }}
                    className="grid size-5 place-items-center rounded text-fg-muted hover:bg-bg-active hover:text-danger"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </span>
              )}
            </div>
          </PageMenu>
        );
      })}
    </div>
  );
}
